import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sparkles, Calendar, Building2, Zap } from "lucide-react";
import SEO from "../components/SEO";

const LOGO_URL = "/logo_concept_1.png";

const originalsFlavors = [
  { name: "Bubblegum Dream", color: "#FFB6C1" },
  { name: "Royal Grape", color: "#9370DB" },
  { name: "Citrus Sunset", color: "#FFA500" },
  { name: "Watermelon Splash", color: "#FF6B6B" },
  { name: "Cherry Jubilee", color: "#DC143C" },
  { name: "Birthday Cake", color: "#FFE4E1" },
  { name: "Blue Raspberry Bliss", color: "#00BFFF" },
];

export default function HomePage() {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title="Cotton Candy Girl | Wholesale Premium Cotton Candy"
        description="Wholesale cotton candy for retailers, resorts, entertainment venues and specialty businesses in Las Vegas and Boise. Home of the original UV-reactive cotton candy."
        keywords="wholesale cotton candy, UV-reactive cotton candy, bulk cotton candy, Las Vegas cotton candy, Boise cotton candy, private label cotton candy"
        canonical="/"
      />
      
      {/* Hero Section */}
      <section className="hero-gradient min-h-screen flex items-center justify-center pt-20 relative overflow-hidden" data-testid="hero-section">
        {/* Decorative clouds */}
        <div className="cloud-shape w-96 h-96 top-20 -left-48 opacity-40"></div>
        <div className="cloud-shape w-64 h-64 top-40 right-20 opacity-30"></div>
        <div className="cloud-shape w-80 h-80 bottom-20 left-1/4 opacity-25"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Hero Product Image */}
          <div className="animate-float mb-8">
            <img 
              src="/product-pink-9pack.png" 
              alt="Cotton Candy Girl - Wholesale Premium Cotton Candy" 
              className="mx-auto max-h-72 md:max-h-96 w-auto rounded-3xl shadow-2xl"
              data-testid="hero-product-image"
            />
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4A4A4A] mb-6 animate-fade-in-up tracking-tight">
            Wholesale Cotton Candy <span className="gradient-text">Made to Stand Out</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-[#888888] max-w-3xl mx-auto mb-4 animate-fade-in-up animate-delay-100 leading-relaxed">
            Shelf-ready cotton candy for retailers, resorts, entertainment venues and specialty businesses in Las Vegas and Boise.
          </p>
          
          {/* UV Tagline */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 text-purple-800 px-6 py-3 rounded-full mb-8 animate-fade-in-up animate-delay-150">
            <Zap size={20} className="text-purple-600" />
            <span className="font-semibold">Home of the Original Naturally UV-Activated Cotton Candy</span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animate-delay-200 mb-6">
            <Link to="/wholesale">
              <Button 
                className="bg-[#FF9EAA] hover:bg-[#FF8FAB] text-white rounded-full px-10 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all btn-scale"
                data-testid="hero-wholesale-btn"
              >
                <Building2 className="mr-2" size={22} />
                Request Wholesale Information
              </Button>
            </Link>
            <Link to="/uv-cotton-candy">
              <Button 
                variant="outline"
                className="border-2 border-purple-400 text-purple-600 hover:bg-purple-50 rounded-full px-10 py-7 text-lg font-semibold transition-all btn-scale"
                data-testid="hero-uv-btn"
              >
                <Zap className="mr-2" size={22} />
                Explore UV Cotton Candy
              </Button>
            </Link>
          </div>
          
          {/* Service Area */}
          <p className="text-[#888888] text-sm animate-fade-in-up animate-delay-300">
            Serving Las Vegas and Boise • Monthly wholesale delivery available
          </p>

          {/* Sparkle decorations */}
          <Sparkles className="absolute top-32 left-1/4 text-yellow-400 animate-sparkle" size={24} />
          <Sparkles className="absolute top-48 right-1/3 text-pink-400 animate-sparkle animate-delay-300" size={20} />
          <Sparkles className="absolute bottom-40 left-1/3 text-purple-400 animate-sparkle animate-delay-500" size={18} />
        </div>
      </section>

      {/* UV Cotton Candy Feature Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white" data-testid="uv-feature-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-purple-500/30 text-purple-200 text-sm font-bold px-4 py-2 rounded-full mb-6">
                OUR SIGNATURE PRODUCT
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                UV-Activated Cotton Candy
              </h2>
              <p className="text-lg text-purple-200 mb-6 leading-relaxed">
                Be the first in your market to offer cotton candy that glows under blacklight. Our Birthday Cake flavor is activated by natural ingredients under UV light—ideal for nightclubs, casinos, resorts, and entertainment venues.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                    <Zap size={14} className="text-white" />
                  </div>
                  <span>Non-toxic and naturally UV-activated</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                    <Zap size={14} className="text-white" />
                  </div>
                  <span>Dye-free Birthday Cake flavor</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                    <Zap size={14} className="text-white" />
                  </div>
                  <span>Available in pre-packaged tubs or live spinning</span>
                </li>
              </ul>
              <Link to="/uv-cotton-candy">
                <Button 
                  className="bg-white text-purple-900 hover:bg-purple-100 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  data-testid="uv-learn-more-btn"
                >
                  Learn More About UV Cotton Candy
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-purple-700/50 rounded-3xl p-8 text-center">
                <Zap className="mx-auto mb-4 text-purple-300" size={48} />
                <p className="text-2xl font-bold mb-2">Naturally UV-Activated</p>
                <p className="text-purple-200">No dyes. No additives. The glow comes from natural ingredients reacting under UV light.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flavors Preview Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 section-pink-light" data-testid="flavors-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mb-4">Wholesale Flavor Gallery</h2>
            <p className="text-lg text-[#888888]">Classic flavors for wholesale orders</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-12">
            {originalsFlavors.map((flavor) => (
              <div 
                key={flavor.name}
                className="flavor-card bg-white rounded-2xl p-4 text-center shadow-md hover:shadow-lg"
                style={{ borderTop: `4px solid ${flavor.color}` }}
                data-testid={`flavor-${flavor.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-3"
                  style={{ backgroundColor: flavor.color, opacity: 0.3 }}
                ></div>
                <p className="font-semibold text-[#4A4A4A] text-sm">{flavor.name}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/flavors">
              <Button 
                variant="outline"
                className="border-2 border-[#FF9EAA] text-[#FF9EAA] hover:bg-pink-50 rounded-full px-8 py-4 font-semibold transition-all"
              >
                View All Flavors
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Wholesale Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="why-wholesale-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mb-4">Why Partner With Us</h2>
            <p className="text-lg text-[#888888]">Built for retail success</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-pink-50 to-white shadow-md">
              <div className="w-16 h-16 rounded-2xl bg-[#FF9EAA] flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">📦</span>
              </div>
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Shelf-Ready Packaging</h3>
              <p className="text-[#888888]">32oz clear, tamper-evident containers with 4-6 week shelf life</p>
            </div>
            
            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-purple-50 to-white shadow-md">
              <div className="w-16 h-16 rounded-2xl bg-purple-500 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🏷️</span>
              </div>
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Flexible Branding</h3>
              <p className="text-[#888888]">Blank, branded, or private-label options to fit your needs</p>
            </div>
            
            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-blue-50 to-white shadow-md">
              <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🚚</span>
              </div>
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">Reliable Delivery</h3>
              <p className="text-[#888888]">Monthly ordering window with consistent mid-month delivery</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 section-blue-light" data-testid="quick-links-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Wholesale */}
            <Link 
              to="/wholesale"
              className="glass rounded-3xl p-8 hover:shadow-lg transition-all group text-center"
              data-testid="quick-link-wholesale"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#FCD34D] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform mx-auto">
                <Building2 className="text-[#92400E]" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#4A4A4A] mb-3">Wholesale Information</h3>
              <p className="text-[#888888] mb-4">
                Learn about our products, ordering process, and submit an inquiry.
              </p>
              <span className="text-[#FF9EAA] font-semibold">Get Started &rarr;</span>
            </Link>

            {/* Events */}
            <Link 
              to="/events"
              className="glass rounded-3xl p-8 hover:shadow-lg transition-all group text-center"
              data-testid="quick-link-events"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#FF9EAA] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform mx-auto">
                <Calendar className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#4A4A4A] mb-3">Live Cotton Candy Events</h3>
              <p className="text-[#888888] mb-4">
Fresh spun cotton candy for select corporate and venue events in Las Vegas or Boise. Accepted by request.
              </p>
              <span className="text-[#FF9EAA] font-semibold">Learn More &rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 hero-gradient" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mb-6">Ready to Stock Cotton Candy Girl?</h2>
          <p className="text-lg text-[#888888] mb-8">
            Join retailers, resorts, and entertainment venues across Las Vegas and Boise.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/wholesale">
              <Button 
                className="bg-[#FF9EAA] hover:bg-[#FF8FAB] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all btn-scale"
                data-testid="cta-wholesale-btn"
              >
                <Building2 className="mr-2" size={20} />
                Request Wholesale Info
              </Button>
            </Link>
            <Link to="/about">
              <Button 
                variant="outline"
                className="border-2 border-[#FF9EAA] text-[#FF9EAA] hover:bg-pink-50 rounded-full px-8 py-6 text-lg font-semibold transition-all btn-scale"
                data-testid="cta-about-btn"
              >
                Learn About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
